// Terms of Use and Privacy Notice, rendered both on the /terms page and
// inside the one-time acceptance gate. The text is the portal's legal
// document (source copy: docs/terms-and-privacy-v1.md) — edit BOTH together,
// and bump TERMS_VERSION only for material changes: every user is re-prompted
// to agree when the version changes.
import React from "react";

export const TERMS_VERSION = "1.1";
export const TERMS_EFFECTIVE = "July 18, 2026";

const h = { fontSize: "1.02rem", fontWeight: 700, margin: "18px 0 6px" };
const p = { margin: "0 0 10px", lineHeight: 1.55 };
const ul = { margin: "0 0 10px 22px", lineHeight: 1.55, padding: 0 };

export default function TermsContent() {
    return (
        <div style={{ fontSize: "0.92rem" }}>
            <p style={{ ...p, fontWeight: 700 }}>
                Version {TERMS_VERSION}<br />
                Effective: {TERMS_EFFECTIVE}
            </p>
            <p style={p}>
                Please read this before using the SEAPA Portal. You will be asked to acknowledge it once,
                and again if it materially changes.
            </p>

            <h3 style={h}>1. Who runs the portal</h3>
            <p style={p}>
                The SEAPA Portal (the "Portal") is owned, developed, and operated by LadderWatch LLC
                ("LadderWatch"), a Washington limited liability company. It is made available to the
                SouthEast Alaska Pilots' Association ("SEAPA") and its members, trainees, and staff for
                use and evaluation. SEAPA does not currently own the Portal. Ownership may transfer to
                SEAPA in the future only if the SEAPA membership approves that arrangement.
            </p>
            <p style={p}>
                In this notice, "we" and "us" means SEAPA and LadderWatch together, and "you" means the
                person using the Portal.
            </p>

            <h3 style={h}>2. Your agreement</h3>
            <p style={p}>
                By clicking "I agree" or by using the Portal, you accept these terms. If you do not agree,
                do not use the Portal and contact the SEAPA office instead.
            </p>

            <h3 style={h}>3. The Portal is a working tool, and it is in beta</h3>
            <p style={p}>
                The Portal is under active development. Features change, and errors are possible. We work
                to keep the information accurate and the service available, but you should expect
                occasional bugs, outages, and incorrect or out-of-date data, and you should report
                anything that looks wrong using the Feedback button.
            </p>

            <h3 style={h}>4. The Portal is not a navigation tool</h3>
            <p style={p}>
                Nothing in the Portal is intended for navigation or may be relied on for navigation.
                Transit briefs, port call times, tides and currents, weather, notices to
                mariners, fishery openings, whale alerts, pilot boat schedules, meeting points, and
                similar content are informational aids only. They may be incomplete, delayed, or wrong.
            </p>
            <p style={p}>
                You are a professional. Your own judgment, your official charts and publications, your
                bridge equipment, and official sources always govern. Neither SEAPA nor LadderWatch
                accepts any responsibility for a navigational decision made in reliance on the Portal.
            </p>

            <h3 style={h}>5. The Portal is not the official record</h3>
            <p style={p}>
                The SEAPA office records are the official record for dispatch, assignments, pay, travel,
                and submitted paperwork. The Portal displays copies of that information and can be
                incomplete or stale. If the Portal and the office disagree, the office is right.
                Submitting a pilot slip, expense report, or other document through the Portal does not
                change who is responsible for its accuracy: you are.
            </p>

            <h3 style={h}>6. Your account</h3>
            <p style={p}>
                Access is by personal login link or password. Your account is yours alone. Do not share
                your login email, forward a login link, or let anyone else use your session. Tell the
                SEAPA office promptly if you believe someone else has used your account. We may suspend or
                remove access to protect the Portal or its data, and access ends when your association
                with SEAPA ends.
            </p>

            <h3 style={h}>7. Electronic signatures</h3>
            <p style={p}>
                The Portal lets you sign documents electronically, including by drawing a signature and by
                reusing a signature you have saved. You agree that a signature you apply through the
                Portal is intended by you to be your signature, with the same force and effect as a
                signature in ink, on every document where you apply it. Do not apply another person's
                signature under any circumstances.
            </p>

            <h3 style={h}>8. Confidential information</h3>
            <p style={p}>
                Dispatch schedules, vessel assignments, crew details, travel plans, training records, and
                member contact information in the Portal are confidential to SEAPA. Use them only for
                SEAPA business. Do not share screenshots or exports outside the association except as
                SEAPA business requires (for example, providing your own records to the Coast Guard).
            </p>

            <h3 style={h}>9. Privacy: what we collect</h3>
            <p style={p}>The Portal stores information needed to run a pilots' association, including:</p>
            <ul style={ul}>
                <li>Your name, email address, phone number, and dispatch port</li>
                <li>License details, including license number, tonnage, and Mariner Reference Number</li>
                <li>Dispatch assignments, schedules, and travel reservations</li>
                <li>Pilot slips, expense reports, receipts, and related paperwork</li>
                <li>Training and continuing education records</li>
                <li>Saved signatures, if you choose to save one</li>
                <li>Feedback you submit, and basic technical records such as the app version and the page you reported from</li>
            </ul>
            <p style={p}>
                The Portal does not collect data for advertising, does not sell or rent your information
                to anyone, and does not share it outside SEAPA's purposes.
            </p>

            <h3 style={h}>10. Privacy: who can see what</h3>
            <p style={p}>
                The Portal is a shared tool for a small association, and most operational information
                (schedules, assignments, travel, rosters) is visible to other authenticated SEAPA members
                and staff by design, the same way the paper dispatch sheet is. Some information is
                restricted further:
            </p>
            <ul style={ul}>
                <li>A saved signature can be read and changed only by its owner.</li>
                <li>Certain records, such as leadership tickets marked private, are limited to the people involved.</li>
                <li>Administrative functions are limited to designated roles.</li>
            </ul>

            <h3 style={h}>11. Privacy: where your data lives</h3>
            <p style={p}>
                Portal data is stored with established cloud service providers in the United States
                (currently Supabase for the database and file storage, Vercel for hosting, and Resend for
                email delivery). These providers process data on our behalf under their own security
                commitments. Copies of some data are kept on your own device so the Portal can work
                offline; protecting your device with a passcode protects that data.
            </p>

            <h3 style={h}>12. Privacy: retention and deletion</h3>
            <p style={p}>
                Operational records (slips, expenses, training records, dispatch history) are kept as long
                as SEAPA needs them for business, regulatory, and record keeping purposes. If you leave
                the association, your access ends, and you may ask the SEAPA office about removal of
                personal information that SEAPA does not need to retain. You may delete your saved
                signature yourself at any time by saving a new one or by asking the developer to remove
                it.
            </p>

            <h3 style={h}>13. No warranty</h3>
            <p style={p}>
                The Portal is provided "as is" and "as available", without warranties of any kind, express
                or implied, including fitness for a particular purpose, accuracy, or uninterrupted
                availability.
            </p>

            <h3 style={h}>14. Limitation of liability</h3>
            <p style={p}>
                To the fullest extent permitted by law, neither SEAPA nor LadderWatch, nor their members,
                managers, officers, or agents, will be liable for any indirect, incidental, special, or
                consequential damages, or for any loss arising from reliance on Portal content, from
                unavailability of the Portal, or from unauthorized access resulting from your failure to
                protect your own login. Nothing in these terms limits liability that cannot be limited
                under applicable law.
            </p>

            <h3 style={h}>15. Changes to these terms</h3>
            <p style={p}>
                We may update these terms from time to time. If a change is material, the Portal will show
                you the new version and ask you to acknowledge it before you continue. The version number
                and date at the top of this document tell you what you have agreed to.
            </p>

            <h3 style={h}>16. Governing law</h3>
            <p style={p}>
                These terms are governed by the laws of the State of Alaska. Any dispute will be resolved
                in the state or federal courts located in Alaska.
            </p>

            <h3 style={h}>17. Contact</h3>
            <p style={p}>
                Questions about these terms or about your data: contact Jill Russell at
                {" "}jill@ladderwatch.com, or use the Feedback button in the Portal.
            </p>
        </div>
    );
}
